import React, { useEffect, useMemo, useState } from "react";
import PageHeader from "../../components/headers/PageHeader";
import "./careers.css";

export default function Careers() {
    const [jobs, setJobs] = useState([]);
    const [search, setSearch] = useState("");
    const [department, setDepartment] = useState("All");
    const [location, setLocation] = useState("All");
    const [type, setType] = useState("All");

    const [selectedJob, setSelectedJob] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [applicationSubmitted, setApplicationSubmitted] = useState(false);
    const [applicationError, setApplicationError] = useState("");

    useEffect(() => {
        fetch("/data/jobs.json")
            .then((res) => res.json())
            .then((data) => {
                if (Array.isArray(data)) {
                    setJobs(data);
                } else {
                    setJobs([]);
                }
            })
            .catch((err) => {
                console.error("Failed to load jobs:", err);
                setJobs([]);
            });
    }, []);

    const departments = ["All", ...new Set(jobs.map((job) => job.department).filter(Boolean))];
    const locations = ["All", ...new Set(jobs.map((job) => job.location).filter(Boolean))];
    const types = ["All", ...new Set(jobs.map((job) => job.type).filter(Boolean))];

    const filteredJobs = useMemo(() => {
        return jobs.filter((job) => {
            const query = search.toLowerCase();

            const matchesSearch =
                job.title?.toLowerCase().includes(query) ||
                job.department?.toLowerCase().includes(query) ||
                job.summary?.toLowerCase().includes(query);

            const matchesDepartment =
                department === "All" || job.department === department;

            const matchesLocation =
                location === "All" || job.location === location;

            const matchesType =
                type === "All" || job.type === type;

            return matchesSearch && matchesDepartment && matchesLocation && matchesType;
        });
    }, [jobs, search, department, location, type]);

    const clearFilters = () => {
        setSearch("");
        setDepartment("All");
        setLocation("All");
        setType("All");
    };

    const openApplicationForm = (job) => {
        setSelectedJob(job);
        setApplicationSubmitted(false);
        setApplicationError("");
    };

    const closeApplicationForm = () => {
        setSelectedJob(null);
        setApplicationSubmitted(false);
        setApplicationError("");
    };

    const handleApplicationSubmit = async (e) => {
        e.preventDefault();

        if (!selectedJob) return;

        setIsSubmitting(true);
        setApplicationError("");

        const form = e.currentTarget;
        const formData = new FormData(form);

        formData.append("jobTitle", selectedJob.title || "");
        formData.append("jobDepartment", selectedJob.department || "");
        formData.append("jobLocation", selectedJob.location || "");
        formData.append("jobType", selectedJob.type || "");
        formData.append("jobMode", selectedJob.mode || "");

        try {
            const response = await fetch("https://formspree.io/f/xaqkpvad", {
                method: "POST",
                body: formData,
                headers: {
                    Accept: "application/json",
                },
            });

            if (response.ok) {
                setApplicationSubmitted(true);
                form.reset();
            } else {
                setApplicationError(
                    "Something went wrong while submitting your application. Please try again."
                );
            }
        } catch (error) {
            console.error(error);
            setApplicationError(
                "Unable to submit your application at this time. Please try again later."
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="careers-page">
            <PageHeader
                title="Careers"
                slogan="Join Eupraxia and contribute to meaningful work in clinical research, data science, and regulatory excellence."
            />

            <section className="careers-section">
                <div className="careers-headingRow">
                    <div>
                        <h2>Current Openings</h2>
                        <p>
                            Explore opportunities across clinical research, regulatory
                            affairs, data management, biostatistics, technology, and
                            operations.
                        </p>
                    </div>

                    <span className="careers-count">
                        {filteredJobs.length} Opening{filteredJobs.length !== 1 ? "s" : ""}
                    </span>
                </div>

                {jobs.length > 0 && (
                    <div className="careers-filterPanel">
                        <div className="careers-searchBox">
                            <label htmlFor="careerSearch">Search roles</label>
                            <input
                                id="careerSearch"
                                type="text"
                                placeholder="Search by title, department, or keyword"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>

                        <div className="careers-filterGrid">
                            <div className="careers-filterGroup">
                                <label>Department</label>
                                <select
                                    value={department}
                                    onChange={(e) => setDepartment(e.target.value)}
                                >
                                    {departments.map((item) => (
                                        <option key={item} value={item}>
                                            {item}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="careers-filterGroup">
                                <label>Location</label>
                                <select
                                    value={location}
                                    onChange={(e) => setLocation(e.target.value)}
                                >
                                    {locations.map((item) => (
                                        <option key={item} value={item}>
                                            {item}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="careers-filterGroup">
                                <label>Employment Type</label>
                                <select
                                    value={type}
                                    onChange={(e) => setType(e.target.value)}
                                >
                                    {types.map((item) => (
                                        <option key={item} value={item}>
                                            {item}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <button
                                type="button"
                                className="careers-clearBtn"
                                onClick={clearFilters}
                            >
                                Clear Filters
                            </button>
                        </div>
                    </div>
                )}

                {filteredJobs.length > 0 ? (
                    <div className="careers-grid">
                        {filteredJobs.map((job) => (
                            <article className="careers-card" key={job.id}>
                                <div className="careers-cardTop">
                                    <span className="careers-status">{job.status}</span>
                                    <span className="careers-type">{job.type}</span>
                                </div>

                                <h3>{job.title}</h3>

                                <p className="careers-summary">{job.summary}</p>

                                <div className="careers-meta">
                                    <div>
                                        <strong>Department</strong>
                                        <span>{job.department}</span>
                                    </div>

                                    <div>
                                        <strong>Location</strong>
                                        <span>{job.location}</span>
                                    </div>

                                    <div>
                                        <strong>Mode</strong>
                                        <span>{job.mode}</span>
                                    </div>

                                    <div>
                                        <strong>Experience</strong>
                                        <span>{job.experience}</span>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    className="careers-applyBtn"
                                    onClick={() => openApplicationForm(job)}
                                >
                                    Apply Now
                                </button>
                            </article>
                        ))}
                    </div>
                ) : (
                    <div className="careers-emptyState">
                        {jobs.length === 0 ? (
                            <>
                                <h3>No positions are open at the moment</h3>
                                <p>
                                    We are not hiring for any roles right now. Please check
                                    back later for future opportunities at Eupraxia.
                                </p>
                            </>
                        ) : (
                            <>
                                <h3>No openings found</h3>
                                <p>
                                    Try adjusting your search or clearing the selected filters.
                                </p>

                                <button type="button" onClick={clearFilters}>
                                    Clear Filters
                                </button>
                            </>
                        )}
                    </div>
                )}
            </section>

            {selectedJob && (
                <div className="careers-modalOverlay">
                    <div className="careers-modal">
                        <div className="careers-modalHeader">
                            <div>
                                <span>Application Form</span>
                                <h2>{selectedJob.title}</h2>
                                <p>{selectedJob.location}</p>
                            </div>

                            <button
                                type="button"
                                className="careers-modalClose"
                                onClick={closeApplicationForm}
                            >
                                ×
                            </button>
                        </div>

                        {applicationSubmitted ? (
                            <div className="careers-applicationSuccess">
                                <h3>Application submitted successfully.</h3>
                                <p>
                                    Thank you for applying. Our HR team will review your
                                    application and contact you if your profile matches the
                                    requirement.
                                </p>

                                <button type="button" onClick={closeApplicationForm}>
                                    Close
                                </button>
                            </div>
                        ) : (
                            <form
                                className="careers-applicationForm"
                                onSubmit={handleApplicationSubmit}
                                encType="multipart/form-data"
                            >
                                <div className="careers-formGrid">
                                    <div className="careers-formGroup">
                                        <label>First Name *</label>
                                        <input name="firstName" type="text" required />
                                    </div>

                                    <div className="careers-formGroup">
                                        <label>Last Name *</label>
                                        <input name="lastName" type="text" required />
                                    </div>

                                    <div className="careers-formGroup">
                                        <label>Email *</label>
                                        <input name="email" type="email" required />
                                    </div>

                                    <div className="careers-formGroup">
                                        <label>Phone</label>
                                        <input name="phone" type="tel" />
                                    </div>

                                    <div className="careers-formGroup">
                                        <label>Location</label>
                                        <input name="candidateLocation" type="text" />
                                    </div>

                                    <div className="careers-formGroup">
                                        <label>LinkedIn Profile</label>
                                        <input
                                            name="linkedinProfile"
                                            type="url"
                                            placeholder="https://linkedin.com/in/..."
                                        />
                                    </div>

                                    <div className="careers-formGroup">
                                        <label>Website</label>
                                        <input
                                            name="website"
                                            type="url"
                                            placeholder="https://..."
                                        />
                                    </div>

                                    <div className="careers-formGroup">
                                        <label>Other Link</label>
                                        <input
                                            name="otherLink"
                                            type="url"
                                            placeholder="https://..."
                                        />
                                    </div>

                                    <div className="careers-formGroup careers-formGroup--full">
                                        <label>Upload CV *</label>
                                        <input
                                            name="cv"
                                            type="file"
                                            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                            required
                                        />
                                        <small>Accepted formats: PDF, DOC, DOCX.</small>
                                    </div>

                                    <div className="careers-formGroup careers-formGroup--full">
                                        <label>Where did you hear about this job? *</label>
                                        <select name="source" required>
                                            <option value="">Select an option</option>
                                            <option value="Company Website">
                                                Company Website
                                            </option>
                                            <option value="Company Employee">
                                                Company Employee
                                            </option>
                                            <option value="LinkedIn">LinkedIn</option>
                                            <option value="Other">Other</option>
                                        </select>
                                    </div>

                                    <div className="careers-formGroup careers-formGroup--full">
                                        <label>
                                            If referred by a company employee, please mention
                                            their name
                                        </label>
                                        <input name="employeeReferralName" type="text" />
                                    </div>

                                    <div className="careers-formGroup careers-formGroup--full">
                                        <label>
                                            Are you able to work in our office where the post is
                                            offered? *
                                        </label>
                                        <select name="officeWorkAvailability" required>
                                            <option value="">Select an option</option>
                                            <option value="Yes">Yes</option>
                                            <option value="No">No</option>
                                            <option value="Need to discuss">
                                                Need to discuss
                                            </option>
                                        </select>
                                    </div>
                                </div>

                                {applicationError && (
                                    <p className="careers-formError">
                                        {applicationError}
                                    </p>
                                )}

                                <div className="careers-modalActions">
                                    <button
                                        type="button"
                                        className="careers-cancelBtn"
                                        onClick={closeApplicationForm}
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        type="submit"
                                        className="careers-submitBtn"
                                        disabled={isSubmitting}
                                    >
                                        {isSubmitting
                                            ? "Submitting..."
                                            : "Submit Application"}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}